import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { convertToEmbedUrl } from '../../utils/videoUtils';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '../../styles/videoSection.css';

const VideoSection = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const videosRef = collection(db, 'videos');
      const q = query(videosRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const videosData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVideos(videosData);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="section video-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">📹 Featured Videos</h2>
            <p className="section-subtitle">Watch our latest updates and highlights</p>
          </div>
          <div className="video-loading">Loading videos...</div>
        </div>
      </section>
    );
  }

  if (videos.length === 0) {
    return null;
  }

  // Group videos into chunks of 4 for mobile view
  const chunkVideos = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const videoChunks = chunkVideos(videos, 4);

  return (
    <section className="section video-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">📹 Featured Videos</h2>
          <p className="section-subtitle">Watch our latest updates and highlights</p>
        </div>
        
        {/* Mobile View: Show 4 videos per slide in 2x2 grid */}
        <div className="mobile-video-slider">
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={20}
            slidesPerView={1}
            slidesPerGroup={1}
            navigation
            pagination={{ clickable: true }}
            className="video-swiper-mobile"
          >
            {videoChunks.map((chunk, chunkIndex) => (
              <SwiperSlide key={`chunk-${chunkIndex}`}>
                <div className="video-grid-mobile">
                  {chunk.map((video) => (
                    <div key={video.id} className="video-card">
                      <div className="video-wrapper">
                        <iframe
                          src={convertToEmbedUrl(video.url)}
                          title={video.title || 'Video'}
                          className="video-iframe"
                          allow="autoplay; encrypted-media; loop"
                          allowFullScreen
                          frameBorder="0"
                        ></iframe>
                      </div>
                      {video.title && (
                        <div className="video-info">
                          <h3 className="video-title">{video.title}</h3>
                          {video.description && (
                            <p className="video-description">{video.description}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Desktop View: Show 3 videos per slide */}
        <div className="desktop-video-slider">
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={20}
            slidesPerView={3}
            slidesPerGroup={3}
            navigation
            pagination={{ clickable: true }}
            className="video-swiper-desktop"
          >
            {videos.map((video) => (
              <SwiperSlide key={video.id}>
                <div className="video-card">
                  <div className="video-wrapper">
                    <iframe
                      src={convertToEmbedUrl(video.url)}
                      title={video.title || 'Video'}
                      className="video-iframe"
                      allow="autoplay; encrypted-media; loop"
                      allowFullScreen
                      frameBorder="0"
                    ></iframe>
                  </div>
                  {video.title && (
                    <div className="video-info">
                      <h3 className="video-title">{video.title}</h3>
                      {video.description && (
                        <p className="video-description">{video.description}</p>
                      )}
                    </div>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
